// The Tour Leader / Guide content model.
//
// Ported from the original static site's `tour-leaders.html`, where the roster
// lived in a hand-edited `guidesData` array at the bottom of the page. This is
// the same shape as a Firestore document, maintained from `/admin/guides`.
//
// One field from that array is deliberately absent: `availability: 'high' |
// 'medium' | 'low'`. It was typed in by hand and went stale the moment a guide
// took a tour. Real availability is /portal's — it holds the tour requests and
// the days off — and is fetched per guide by the `guideAvailability` callable.
// See `types_portal.ts` for that wire format, and `portalGuideId` below for how
// a guide here is matched to a guide there.

import { formatMonthYear, parseCalendarDate } from '@/util/util_format';
import type { HistoryInfoType } from './types_history';

// Firestore collection name.
export const coll_guides = 'guides';

// Archived edit-history steps, written by the `guides_written` Cloud Function.
// The name is derived server-side as `{collection} + 'history'`, so the two must
// be kept in step. Readable by Eighty Days admins only.
export const coll_guidesHistory = coll_guides + 'history';

/**
 * What a guide is expert in. The values are the filter keys from the original
 * page and are stored on the document, so renaming one is a data migration.
 *
 * Eleven, where the page's filter bar offered eight: 'culture', 'technology'
 * and 'outdoors' were used by the roster but had no filter button of their own.
 * They are kept as tags — they still appear on a profile — and
 * `guideFilterSpecialties` below is what the filter bar renders.
 */
export type GuideSpecialtyType =
  | 'art'
  | 'history'
  | 'food'
  | 'cars'
  | 'nature'
  | 'architecture'
  | 'fashion'
  | 'culture'
  | 'technology'
  | 'outdoors'
  | 'nightlife';

export const guideSpecialtyLabels: Record<GuideSpecialtyType, string> = {
  art: 'Art & Culture',
  history: 'History',
  food: 'Food & Culinary',
  cars: 'Cars & Automotive',
  nature: 'Nature & Outdoors',
  architecture: 'Architecture',
  fashion: 'Fashion',
  culture: 'Culture',
  technology: 'Technology',
  outdoors: 'Outdoors',
  nightlife: 'Bar Hopping & Nightlife',
};

/** Every specialty, in the order the admin form and profile pages list them. */
export const guideSpecialties = Object.keys(guideSpecialtyLabels) as GuideSpecialtyType[];

/**
 * The specialties the public filter bar offers, in the original page's order.
 *
 * A subset rather than the whole list because a filter nobody can satisfy is
 * worse than no filter: the public page narrows this again to the specialties
 * some active guide actually holds, the same way the catalogue only offers the
 * regions it has experiences in.
 */
export const guideFilterSpecialties: GuideSpecialtyType[] = [
  'art',
  'history',
  'food',
  'cars',
  'nature',
  'architecture',
  'fashion',
  'nightlife',
];

export type GuideStatusType = 'active' | 'inactive';

export const guideStatusLabels: Record<GuideStatusType, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export interface GuideType {
  id: string;

  // Identity.
  name: string;
  /** The guide's name in Japanese, shown under the English one. */
  nameJp: string;
  photo: string;
  bio: string;

  // Expertise.
  specialties: GuideSpecialtyType[];
  /** Spoken languages, as written for guests: 'English', 'Japanese', … */
  languages: string[];

  /**
   * The day this guide started guiding, `yyyy-mm-dd`, or '' if unknown.
   *
   * Stored instead of the years of experience it replaced, because a stored
   * count is only true on the day it is typed: "12 years" entered in 2026 still
   * says 12 in 2031, and nobody revisits a roster to age it. A start date is a
   * fact that does not move, and `guideYearsGuiding` derives the count from it
   * whenever the page is rendered.
   *
   * A day rather than a year because the roster holds people, and a guide who
   * started in November should not be credited with the year in January. Where
   * only the year is remembered, any day in it is close enough — the derived
   * count is shown as a whole number of years, never a precise anniversary.
   */
  guidingSince: string;

  /**
   * How this guide is known to /portal — the id of their `users/{id}` document
   * there, which is also the id of their `guidecalendars` document.
   *
   * Blank until somebody fills it in, and that is a meaningful state rather than
   * an oversight: a guide with no portal id has no calendar to query, so the
   * public page shows their profile and says availability is on request instead
   * of showing an empty month. It is not an email address — the static page's
   * `calendarId` was one, but /portal keys guides by uid.
   */
  portalGuideId: string;

  status: GuideStatusType;

  /** Ascending; ties broken by name. Lets the roster be ordered editorially. */
  displayOrder: number;

  createdAt?: string;
  updatedAt?: string;

  // Persisted edit history, populated by autosave. Absent until the first edit.
  history?: HistoryInfoType;
}

/**
 * A stored guide, with anything added to the type since it was written filled in
 * from the defaults. The counterpart of `normalisePartner` — see there for why a
 * cast is not enough, and why defaulting here writes nothing back.
 *
 * `experienceYears` is read here and nowhere else: documents written before
 * `guidingSince` existed carry a count instead of a date, and dropping it would
 * silently blank the experience line on every guide already on the roster. The
 * count is turned back into the start date it implies (see
 * `guidingSinceFromYears`), and the next save of that field replaces it with a
 * real one.
 */
export function normaliseGuide(data: Partial<GuideType> & LegacyGuideFieldsType, id: string): GuideType {
  const { experienceYears, ...guide } = data;
  const normalised = { ...makeEmptyGuide(), ...guide, id };
  if (!normalised.guidingSince && experienceYears) {
    normalised.guidingSince = guidingSinceFromYears(experienceYears);
  }
  return normalised;
}

/** Fields no longer on `GuideType` that stored documents may still carry. */
interface LegacyGuideFieldsType {
  /** Replaced by `guidingSince`; a hand-entered number of years. */
  experienceYears?: number;
}

/**
 * The start date a stored "N years of experience" stands for: the first of the
 * current month, N years ago.
 *
 * Deliberately not 1 January of that year — that would add up to eleven months
 * of experience nobody claimed, and could round the derived count straight up to
 * N + 1 within the year. Anchoring to today's month makes the count read back as
 * exactly the N that was stored, until the anniversary genuinely passes.
 */
function guidingSinceFromYears(years: number, today = new Date()): string {
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  return `${today.getFullYear() - Math.floor(years)}-${month}-01`;
}

export function makeEmptyGuide(): GuideType {
  return {
    id: '',
    name: '',
    nameJp: '',
    photo: '',
    bio: '',
    specialties: [],
    languages: ['English', 'Japanese'],
    guidingSince: '',
    portalGuideId: '',
    status: 'active',
    displayOrder: 0,
  };
}

export type GuideTourOptionType = {
  key: 'full-day' | 'daytime' | 'afternoon';
  label: string;
  hours: string;
};

/**
 * The tour lengths a concierge may ask for, on a day the guide is free.
 *
 * A preference, not a constraint, and the same three whatever the date — which
 * is what /portal's whole-day answer permits it to be. Their days-off records
 * have no morning or afternoon in them, so a calendar claiming a guide was free
 * only until lunch would be inventing the part that mattered. Better to say the
 * day is open, let the partner say what they want of it, and let the person
 * answering the request settle the hours.
 *
 * No fee, either. What a tour leader costs depends on the day, the party and
 * what is being asked of them, so a figure here would be a quote nobody made.
 */
export const guideTourOptions: GuideTourOptionType[] = [
  { key: 'full-day', label: 'Full Day — 8 hours', hours: '09:00 - 18:00' },
  { key: 'daytime', label: 'Half Day — Day Time', hours: '09:00 - 13:00' },
  { key: 'afternoon', label: 'Half Day — Afternoon', hours: '14:00 - 18:00' },
];

/** Roster order: `displayOrder` ascending, then name, so the list is stable. */
export function compareGuides(a: GuideType, b: GuideType): number {
  return a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);
}

/**
 * Whole years of guiding as of `today`, or undefined when `guidingSince` is
 * blank, unreadable, or a date that has not arrived yet.
 *
 * Whole years, floored: someone eleven months in has not been guiding a year,
 * and a roster that rounds up is overselling the person on it.
 */
export function guideYearsGuiding(guidingSince: string, today = new Date()): number | undefined {
  const start = parseCalendarDate(guidingSince);
  if (!start) return undefined;
  let years = today.getFullYear() - start.getFullYear();
  const monthsIn = today.getMonth() - start.getMonth();
  if (monthsIn < 0 || (monthsIn === 0 && today.getDate() < start.getDate())) years -= 1;
  return years < 0 ? undefined : years;
}

/**
 * The experience line for a profile: "12 years", or "Less than a year" for a
 * guide who started inside the last twelve months. '—' when no start date has
 * been filled in — the same blank the years field used to render for 0.
 */
export function formatGuideExperience(guidingSince: string, today = new Date()): string {
  const years = guideYearsGuiding(guidingSince, today);
  if (years === undefined) return '—';
  if (years < 1) return 'Less than a year';
  return `${years} ${years === 1 ? 'year' : 'years'}`;
}

/** "Guiding since March 2018", or '' when the start date is not set. */
export function formatGuidingSince(guidingSince: string): string {
  const month = formatMonthYear(guidingSince);
  return month ? `Guiding since ${month}` : '';
}

/**
 * Parses the comma-separated languages field back into a list.
 *
 * Free text rather than a fixed set on purpose: the roster already spans more
 * languages than a dropdown would be worth maintaining, and a guide who speaks
 * one we have not thought of should not need a code change to say so.
 */
export function parseGuideLanguages(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
